/**
 * Vendor source for the product list section ("Show products from: the same brand as this
 * product").
 *
 * Shopify's related-products engine blends collection, product type and vendor with its own
 * weighting and offers no way to say "same brand first". On this store that is the problem: a
 * catch-all collection like "Gifts Everyone Loves" behaves like a best-seller list, so products
 * in it drag loose matches ahead of same-brand ones.
 *
 * Liquid cannot list products by vendor on a product page either — vendor collections have no
 * reliable handle (`/collections/galaxy-rose` resolves because a manual collection happens to
 * share the name; `/collections/melodies-by-abi` 404s). So the list is built here, in two passes:
 *
 *   1. Same-vendor products, from Shopify's automatic vendor collection page
 *      (`/collections/vendors?q=<vendor>`).
 *   2. Shopify's related products, used only to top up when the brand has too few.
 *
 * Both passes ask the Section Rendering API for *this same section*, so the cards are the
 * theme's own product cards — there is no second card markup to keep in sync.
 *
 * The ordering logic is exported as pure functions so it can be tested without a DOM.
 */

const ITEM_SELECTOR = '[data-vpl-item]';

/**
 * Split a comma-separated vendor list into normalized names.
 * @param {string | undefined | null} raw
 * @returns {string[]}
 */
export function parseVendorList(raw) {
  return String(raw ?? '')
    .split(',')
    .map((vendor) => vendor.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * A vendor is skipped when it is empty or the merchant listed it as a catch-all.
 *
 * Shopify defaults a product's vendor to the shop name when staff leave it blank, so a house
 * vendor groups products that have nothing in common. Grouping by it would be worse than
 * Shopify's related products, which is the whole point of the vendor source.
 *
 * @param {string | undefined | null} vendor
 * @param {string | undefined | null} excluded - comma-separated vendor names
 * @returns {boolean}
 */
export function isVendorSkipped(vendor, excluded) {
  const name = String(vendor ?? '')
    .trim()
    .toLowerCase();
  if (!name) return true;
  return parseVendorList(excluded).includes(name);
}

/**
 * Order the final list: same-vendor products first, related products only as filler.
 *
 * Drops the product being viewed, drops duplicates across the two sources, and caps the result.
 *
 * @param {string[]} vendorHandles - handles from the vendor pass, in the order the store returned them
 * @param {string[]} relatedHandles - handles from Shopify's related pass
 * @param {{ currentHandle?: string, max?: number }} [options]
 * @returns {string[]}
 */
export function orderProducts(vendorHandles, relatedHandles, options = {}) {
  const { currentHandle = '', max = 4 } = options;
  const limit = Number.isFinite(max) && max > 0 ? Math.floor(max) : 0;
  const seen = new Set();
  const ordered = [];

  if (currentHandle) seen.add(currentHandle);

  for (const handle of [...(vendorHandles ?? []), ...(relatedHandles ?? [])]) {
    if (ordered.length >= limit) break;
    if (!handle || seen.has(handle)) continue;
    seen.add(handle);
    ordered.push(handle);
  }

  return ordered;
}

/**
 * Vendor collection URL for the Section Rendering API.
 *
 * `/collections/vendors?q=<vendor>` is Shopify's automatic per-vendor collection page. Asking for
 * it with `section_id` renders this section against that page's products. The id must be the
 * fully-qualified runtime form (`template--<themeId>__<key>`) that `{{ section.id }}` emits — the
 * short key from the template file returns 404.
 *
 * @param {string} collectionsUrl - routes.collections_url (e.g. "/collections")
 * @param {string} vendor
 * @param {string} sectionId
 * @returns {string}
 */
export function buildVendorUrl(collectionsUrl, vendor, sectionId) {
  const params = new URLSearchParams({
    q: vendor,
    section_id: sectionId,
    sort_by: 'best-selling',
  });
  return `${collectionsUrl}/vendors?${params}`;
}

/**
 * Shopify related-products URL for the Section Rendering API.
 *
 * @param {string} recommendationsUrl - routes.product_recommendations_url
 * @param {string} productId
 * @param {string} sectionId
 * @param {number} limit
 * @returns {string}
 */
export function buildRelatedUrl(recommendationsUrl, productId, sectionId, limit) {
  const params = new URLSearchParams({
    product_id: productId,
    section_id: sectionId,
    intent: 'related',
    limit: String(limit),
  });
  return `${recommendationsUrl}?${params}`;
}

if (typeof HTMLElement !== 'undefined' && typeof customElements !== 'undefined') {
  class VendorProductList extends HTMLElement {
    /** @type {IntersectionObserver | null} */
    #observer = null;

    #loaded = false;

    connectedCallback() {
      // Same deferral as the stock recommendations section: nothing is fetched until the shopper
      // is close to scrolling it into view.
      this.#observer = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0]?.isIntersecting) return;
          observer.disconnect();
          this.#load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.#observer.observe(this);
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
      this.#observer = null;
    }

    get #grid() {
      return this.querySelector('[data-vpl-grid]');
    }

    async #load() {
      if (this.#loaded) return;
      this.#loaded = true;

      const { sectionId, productId, productHandle, vendor, excludedVendors, collectionsUrl, recommendationsUrl } =
        this.dataset;
      const max = Number(this.dataset.max) || 4;
      const fillWithRelated = this.dataset.fillWithRelated === 'true';

      if (!sectionId || !this.#grid) return;

      const skipVendor = isVendorSkipped(vendor, excludedVendors);

      /** @type {Map<string, Element>} */
      const cards = new Map();
      const vendorHandles = [];
      const relatedHandles = [];

      if (!skipVendor && vendor && collectionsUrl) {
        const items = await this.#fetchItems(buildVendorUrl(collectionsUrl, vendor, sectionId));
        for (const item of items) {
          const handle = item.getAttribute('data-vpl-handle');
          if (!handle || cards.has(handle)) continue;
          cards.set(handle, item);
          vendorHandles.push(handle);
        }
      }

      const found = vendorHandles.filter((handle) => handle !== productHandle).length;

      if (skipVendor || (fillWithRelated && found < max)) {
        if (productId && recommendationsUrl) {
          const items = await this.#fetchItems(buildRelatedUrl(recommendationsUrl, productId, sectionId, max + 2));
          for (const item of items) {
            const handle = item.getAttribute('data-vpl-handle');
            if (!handle || cards.has(handle)) continue;
            cards.set(handle, item);
            relatedHandles.push(handle);
          }
        }
      }

      const handles = orderProducts(vendorHandles, relatedHandles, { currentHandle: productHandle, max });

      if (!handles.length) {
        // Nothing relevant to show beats a heading over an empty row.
        this.hidden = true;
        return;
      }

      const grid = this.#grid;
      if (!grid) return;
      grid.replaceChildren(...handles.map((handle) => /** @type {Element} */ (cards.get(handle))));
      this.dataset.vplLoaded = 'true';
    }

    /**
     * Fetch a Section Rendering API URL and return this section's rendered product cards.
     * @param {string} url
     * @returns {Promise<Element[]>}
     */
    async #fetchItems(url) {
      try {
        const response = await fetch(url);
        if (!response.ok) return [];
        const html = document.createElement('div');
        html.innerHTML = await response.text();
        return Array.from(html.querySelectorAll(ITEM_SELECTOR));
      } catch (error) {
        console.error('Vendor product list error:', error instanceof Error ? error.message : error);
        return [];
      }
    }
  }

  if (!customElements.get('vendor-product-list')) {
    customElements.define('vendor-product-list', VendorProductList);
  }
}
